import { gql, useQuery } from "@apollo/client";
import {
  Button,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { Checkbox, IconButton, TextField } from "material-ui";
import { ContentAdd } from "material-ui/svg-icons";
import { observer } from "mobx-react";
import User, { AccountType } from "models/user";
import * as React from "react";
import { Link } from "react-router-dom";
import { UsersPageUiStore } from "stores";
import styled from "styled-components";
import EditUserInfo from "./EditUserInfo";
import VismaInput from "./VismaInput";

const GET_USER_CASKS = gql`
  query GetUserCasks($userId: String!) {
    adminScope {
      casks(where: { caskUsers: { some: { user: { id: { eq: $userId } } } } }) {
        nodes {
          id
          vismaCaskNumber
          caskSign
          status
        }
      }
    }
  }
`;

const Input = styled(TextField)`
  margin-right: 20px;
  display: block !important;
`;

const AddUserWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
`;

const StyledForm = styled.form`
  > * {
    margin-top: 20px;
  }
`;

const CaskItem = styled(Link)`
  display: block;
  padding: 6px 0;
  font-size: 14px;
  color: #333;
  text-decoration: none;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    color: #c00;
  }
`;

interface UserCasksListProps {
  userId: string;
}

const UserCasksList: React.FC<UserCasksListProps> = ({ userId }) => {
  const { loading, data } = useQuery(GET_USER_CASKS, { variables: { userId } });
  const casks = data?.adminScope?.casks?.nodes ?? [];

  return (
    <>
      <Heading size="sm" color="gray.500" mt={6} mb={2}>
        Kopplade fat
      </Heading>
      {loading ? (
        <Text fontSize="sm" color="gray.400">
          Laddar...
        </Text>
      ) : casks.length === 0 ? (
        <Text fontSize="sm" color="gray.400">
          Inga fat kopplade till denna användare
        </Text>
      ) : (
        casks.map((cask: any) => (
          <CaskItem key={cask.id} to={`/casks/${cask.id}`}>
            {cask.vismaCaskNumber ?? `Fat #${cask.id}`}
            {cask.caskSign ? ` — ${cask.caskSign}` : ""}
          </CaskItem>
        ))
      )}
    </>
  );
};

export interface Props {
  user: User;
  onClose: () => void;
  onSaveClick: () => void;
  userPageUiStore: UsersPageUiStore;
}

@observer
class EditUserModal extends React.Component<Props> {
  vismaInputs() {
    const { userPageUiStore: store, user } = this.props;
    return store.vismaInputs.$.map((inputs, index) => {
      const { vismaCustomerNumber, vismaId, id } = inputs.$;
      const vismaUser = user.vismaUsers.find((u) => u.id === id.$);
      let label: string | undefined;
      if (vismaUser) {
        label =
          vismaUser.accountType === AccountType.Company
            ? vismaUser.orgName
            : `${vismaUser.firstName} ${vismaUser.lastName}`;
      }
      return (
        <VismaInput
          key={index}
          vismaCustomerNumber={vismaCustomerNumber}
          vismaId={vismaId}
          label={label}
          onDelete={() => store.deleteVismaRow(index)}
          showButton={store.vismaInputs.$.length > 1}
        />
      );
    });
  }

  render() {
    const { onClose, onSaveClick, user, userPageUiStore } = this.props;
    return (
      <Modal isOpen={true} onClose={onClose} scrollBehavior="inside" size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Redigera användare</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <EditUserInfo user={user} />
            <Divider my={4} />
            <StyledForm
              onSubmit={(evt: React.FormEvent<HTMLFormElement>) => {
                evt.preventDefault();
                onSaveClick();
              }}
            >
              <div style={{ display: "flex", gap: "20px" }}>
                <Input
                  style={{ marginRight: 0, flex: 1 }}
                  value={userPageUiStore.firstName.value}
                  errorText={userPageUiStore.firstName.error}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    userPageUiStore.firstName.onChange(e.currentTarget.value)
                  }
                  floatingLabelText="Förnamn"
                />
                <Input
                  style={{ marginRight: 0, flex: 1 }}
                  value={userPageUiStore.lastName.value}
                  errorText={userPageUiStore.lastName.error}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    userPageUiStore.lastName.onChange(e.currentTarget.value)
                  }
                  floatingLabelText="Efternamn"
                />
              </div>
              <Input
                type="email"
                style={{ marginRight: 0, width: "100%" }}
                value={userPageUiStore.email.value}
                errorText={userPageUiStore.email.error}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  userPageUiStore.email.onChange(e.currentTarget.value)
                }
                floatingLabelText="E-postadress"
              />
              <Checkbox
                label="Verifierad e-postadress"
                checked={userPageUiStore.emailConfirmed.value}
                onCheck={(_e: any, checked: boolean) =>
                  userPageUiStore.emailConfirmed.onChange(checked)
                }
              />
              {this.vismaInputs()}
            </StyledForm>
            <AddUserWrapper>
              <p>Lägg till användare</p>
              <IconButton onClick={() => userPageUiStore.addVismaInput()}>
                <ContentAdd />
              </IconButton>
            </AddUserWrapper>
            {userPageUiStore.saveError && (
              <Text color="red.500" fontSize="sm">
                {userPageUiStore.saveError}
              </Text>
            )}
            <UserCasksList userId={user.id} />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Avbryt
            </Button>
            <Button
              colorScheme="red"
              onClick={onSaveClick}
              isDisabled={userPageUiStore.form.hasError}
            >
              Spara
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
}

export default EditUserModal;
